/* Build-in hooks */
import { createContext, useContext, useEffect, useState } from "react";

/* Third-party libraries */
import { fetchQueue } from "../services/userThunks";

/* Custom hooks */
import { useThunk } from "../hooks/useThunk";

// Create a context for Supreme Edits
const SupremeEditsContext = createContext();

// Custom hook to access the Supreme Edits context
export const useSupremeEditsPlayer = () => {
	const context = useContext(SupremeEditsContext);
	if (!context)
		throw new Error(
			"useSupremeEditsPlayer must be used within a SupremeEditsProvider"
		);
	return context;
};

// Provider component to manage the state and logic for Supreme Edits
export const SupremeEditsProvider = ({ children }) => {
	const [supremeCycle, setSupremeCycle] = useState([]); // List of tracks
	const [playingRowType, setPlayingRowType] = useState(null);
	const [playlistName, setPlaylistName] = useState(null); // Name of the current playlist
	const [isPlaying, setIsPlaying] = useState(false); // Playback state (playing or paused)
	const [currentIndex, setCurrentIndex] = useState(0); // Index of the currently selected track
	const [currentTrack, setCurrentTrack] = useState(
		supremeCycle[currentIndex]
	); // Currently selected track
	const [trackLoading, setTrackLoading] = useState(false); // Loading state for tracks
	const [removingIndex, setRemovingIndex] = useState(null); // Index of the track to be removed
	const [editIndex, setEditIndex] = useState(0); // Index of the track being edited
	const [versionIndex, setVersionIndex] = useState(0); // Index of the version being edited
	const [playerQueue, setPlayerQueue] = useState([]); // Queue of tracks for playback
	const [queueUpdateTrigger, setQueueUpdateTrigger] = useState(true); // Trigger for queue updates
	const [isManualPlay, setIsManualPlay] = useState(false); // Flag to indicate manual play action

	const [doFetchQueue] = useThunk(fetchQueue);

	// log all states when they updates
	useEffect(() => {
		console.log("SupremeEditsProvider states updated:", {
			currentIndex,
			versionIndex,
			editIndex,
			currentTrack,
			playlistName,
			playingRowType,
			supremeCycle,
			isPlaying,
			trackLoading,
			removingIndex,
			playerQueue,
			queueUpdateTrigger,
			isManualPlay,
		});
	}, [
		supremeCycle,
		playingRowType,
		playlistName,
		isPlaying,
		currentIndex,
		currentTrack,
		trackLoading,
		removingIndex,
		editIndex,
		versionIndex,
		playerQueue,
		queueUpdateTrigger,
		isManualPlay,
	]);

	// Sync playerQueue with Redux store when queue changes
	useEffect(() => {
		fetchAllQueue();
	}, [doFetchQueue, queueUpdateTrigger]);

	// Fetch the queue when the component mounts
	const fetchAllQueue = async () => {
		try {
			// Only fetch if user is authenticated
			const token = localStorage.getItem("token");
			if (!token) {
				return;
			}
			
			const result = await doFetchQueue();
			if (result?.success) {
				setPlayerQueue(result.response.data.queue || []);
			}
		} catch (error) {
			console.error("Failed to fetch queue:", error);
		}
	};

	// Move to the next track in the supremeCycle, looping back to the start if at the end
	const handleNext = () => {
		const currentArray =
			playingRowType === "queue" ? playerQueue : supremeCycle;

		if (playingRowType === "queue") {
			setSupremeCycle(playerQueue);
		}

		if (playingRowType === "edit") {
			const availableEditsCount = currentTrack?.editInfos?.length || 0;

			if (editIndex < availableEditsCount - 1) {
				setEditIndex((i) => i + 1);
			} else {
				setEditIndex(1); // Loop back to the first edit
			}
			return;
		}

		if (playingRowType === "version") {
			const availableVersionsCount =
				currentTrack?.editInfos?.[editIndex]?.versions.length || 0;
			if (versionIndex < availableVersionsCount - 1) {
				setVersionIndex((i) => i + 1);
			} else {
				setVersionIndex(0); // Loop back to the first version
			}
			return;
		}

		if (currentIndex < currentArray.length - 1) {
			setCurrentIndex((i) => i + 1);
		} else {
			setCurrentIndex(0); // Loop back to the start
		}
	};

	// Move to the previous track in the supremeCycle, looping back to the end if at the start
	const handlePrev = () => {
		const currentArray =
			playingRowType === "queue" ? playerQueue : supremeCycle;

		if (playingRowType === "queue") {
			setSupremeCycle(playerQueue);
		}

		if (playingRowType === "edit") {
			const availableEditsCount = currentTrack?.editInfos?.length || 0;
			if (editIndex > 1) {
				setEditIndex((i) => i - 1);
			} else {
				setEditIndex(availableEditsCount - 1);
			}
			return;
		}

		if (playingRowType === "version") {
			const availableVersionsCount =
				currentTrack?.editInfos?.[editIndex]?.versions.length || 0;
			if (versionIndex > 0) {
				setVersionIndex((i) => i - 1);
			} else {
				setVersionIndex(availableVersionsCount - 1);
			}
			return;
		}

		if (currentIndex > 0) {
			setCurrentIndex((i) => i - 1);
		} else {
			setCurrentIndex(currentArray.length - 1); // Loop back to the end
		}
	};

	// Update the current track whenever the currentIndex or supremeCycle changes
	useEffect(() => {
		setCurrentTrack(supremeCycle[currentIndex]);
	}, [currentIndex, supremeCycle]);

	// Remove a track from the supremeCycle based on removingIndex
	useEffect(() => {
		if (removingIndex !== null && supremeCycle.length > 0) {
			setIsPlaying(false);
			const updatedSongBook = supremeCycle.filter(
				(_, index) => index !== removingIndex
			);
			setSupremeCycle(updatedSongBook);
			setRemovingIndex(null); // Reset removing index after removal
		}
	}, [currentIndex, removingIndex, supremeCycle]);

	// Ensure editIndex stays within the range of the supremeCycle
	useEffect(() => {
		if (editIndex < 0) {
			setEditIndex(0);
		} else if (editIndex >= currentTrack?.editInfos?.length) {
			setEditIndex(currentTrack?.editInfos?.length - 1);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentTrack]);

	// Ensure versionIndex stays within the range of the currentTrack's versions
	// TEMPORARILY DISABLED to debug the version selection issue
	/*
	useEffect(() => {
		if (currentTrack && currentTrack.editInfos?.[editIndex]?.versions) {
			const maxVersionIndex = currentTrack.editInfos[editIndex].versions.length - 1;
			
			// Only intervene if versionIndex is clearly invalid (negative or way out of bounds)
			if (versionIndex < 0) {
				console.log('Fixing negative versionIndex:', versionIndex, '→ 0');
				setVersionIndex(0);
			} else if (maxVersionIndex >= 0 && versionIndex > maxVersionIndex) {
				// Don't reset immediately - add a delay to avoid interfering with intentional state changes
				const timeoutId = setTimeout(() => {
					console.log('Fixing out-of-bounds versionIndex:', versionIndex, '→', maxVersionIndex);
					setVersionIndex(maxVersionIndex);
				}, 100);
				
				return () => clearTimeout(timeoutId);
			}
		}
	}, [versionIndex, currentTrack, editIndex]);
	*/

	// Helper function to remove a track from the queue and adjust currentIndex if needed
	const removeFromQueue = (indexToRemove) => {
		const newQueue = playerQueue.filter((_, index) => index !== indexToRemove);

		// If removing from an empty or single-item queue
		if (newQueue.length === 0) {
			setPlayerQueue([]);
			// If we were playing from queue and it's now empty, stop playing
			if (playingRowType === "queue") {
				setSupremeCycle([]);
				setIsPlaying(false);
				setPlayingRowType(null);
				setCurrentIndex(0);
			}
			return;
		}

		// Store the current track before any updates to check if it will change
		const currentPlayingTrack = playingRowType === "queue" ? playerQueue[currentIndex] : null;

		// Update the queue first
		setPlayerQueue(newQueue);

		// If we're playing from queue, also update supremeCycle to keep them in sync
		if (playingRowType === "queue") {
			setSupremeCycle(newQueue);
		}

		// If the current track is in the queue, handle index adjustment carefully
		if (playingRowType === "queue" && typeof currentIndex === "number") {
			if (indexToRemove < currentIndex) {
				// If removed track is before current, shift current index back
				// The track itself doesn't change, just its position
				setCurrentIndex(currentIndex - 1);
			} else if (indexToRemove === currentIndex) {
				// If removed track is the currently playing one
				if (currentIndex >= newQueue.length) {
					// If we removed the last track, play the new last track
					setCurrentIndex(newQueue.length - 1);
				}
				// If currentIndex < newQueue.length, the track at currentIndex will be different
				// This will naturally trigger the player to load the new track
			}
			// If indexToRemove > currentIndex, no adjustment needed - current track stays the same
		}
	};

	// Function to close/stop the Supreme Edits player
	const closePlayer = () => {
		setIsPlaying(false);
		setCurrentTrack(null);
		setSupremeCycle([]);
		setCurrentIndex(0);
		setPlayingRowType(null);
	};

	// Return the context provider with the state and functions as its value
	return (
		<SupremeEditsContext.Provider
			value={{
				currentIndex, // Index of the currently selected track
				currentTrack, // Currently selected track object
				editIndex, // Index of the track being edited
				setEditIndex, // Function to update the edit index
				versionIndex, // Index of the version being edited
				setVersionIndex, // Function to update the version index
				handleNext, // Function to move to the next track
				handlePrev, // Function to move to the previous track
				isPlaying, // Boolean indicating if a track is currently playing
				playingRowType, // ID of the currently playing track
				setCurrentIndex, // Function to update the current track index
				setIsPlaying, // Function to update the playback state
				setPlayingRowType, // Function to update the ID of the currently playing track
				setRemovingIndex, // Function to set the index of the track to be removed
				setSupremeCycle, // Function to update the list of tracks
				supremeCycle, // Array containing the list of tracks
				playlistName, // Name of the current playlist
				setPlaylistName, // Function to update the playlist name,
				playerQueue, // Queue of tracks for playback
				setPlayerQueue, // Function to update the player queue
				removeFromQueue, // Helper function to remove track from queue with index adjustment
				trackLoading, // Boolean indicating if a track is currently loading
				setTrackLoading, // Function to update the track loading state
				setQueueUpdateTrigger, // Function to trigger queue updates
				isManualPlay, // Flag to indicate manual play action
				setIsManualPlay, // Function to set manual play flag
				closePlayer, // Function to close/stop the player
			}}
		>
			{children}{" "}
			{/* Render the children components inside the provider */}
		</SupremeEditsContext.Provider>
	);
};
